import type { ISelectedWell } from "stores/map";

import type { MapEntity } from "api/map";

import { isSubordinateWell } from "./isSubordinateWell";

/**
 * In group mode, `selectedWells` contains group entities whose keys are group
 * IDs (not individual UWIs) and whose `memberUwis` lists the constituent wells.
 * The 3D scene service expects a flat map of real UWI → well entry.
 *
 * This function expands group entries to their member UWIs, propagating the
 * group's color to each member. Single-mode entries are passed through as-is.
 */
export function expandWellsForScene(
  selectedWells: Readonly<Record<string, ISelectedWell>>
): Record<string, ISelectedWell> {
  const result: Record<string, ISelectedWell> = {};
  for (const well of Object.values(selectedWells)) {
    if (well.memberUwis && well.memberUwis.length > 0) {
      // Group entity — give each member UWI the group's color
      for (const uwi of well.memberUwis) {
        result[uwi] = { color: well.color, Uwi: uwi, group: well.group };
      }
    } else {
      result[well.Uwi] = well as ISelectedWell;
    }
  }
  return result;
}

export interface BuildWellsForSceneInput {
  /** Current selection (group entities or individual wells). */
  selectedWells: Readonly<Record<string, ISelectedWell>>;
  /** Filtered set from mapEntities; `null` when not yet loaded. */
  mapEntities: readonly MapEntity[] | null;
  /** Whether the map is in global group mode. */
  isMapGroupMode: boolean;
  /** UWIs intersecting the drawn XDA line. */
  xdaLineWells: readonly string[];
  /** Flat uwi → color lookup derived from entitiesOnMap (the active-filter gate). */
  groupModeUwiColors: Readonly<Record<string, string>>;
}

const FALLBACK_GROUP_COLOR = "#1f77b4";

/**
 * Pure derivation of the flat `uwi → well` payload sent to the 3D scene service.
 *
 * This is the single source of truth for "what wells belong in the scene right
 * now", folding together the current selection, the active filter (mapEntities),
 * the drawn XDA line, and group mode. Extracted verbatim from the former inline
 * logic in `ThreeDViewer.updateScene` so it can be unit-tested in isolation.
 *
 * Rules:
 * - Single mode with a resolved filtered set: intersect mapEntities with the
 *   expanded selection (guards against a stale set after deselect), always carry
 *   subordinate leg/inj wells (the filter endpoint omits them), and fall back to
 *   the full expanded selection if the intersection is empty (filter not yet
 *   resolved for this selection).
 * - An empty `mapEntities` ([]) means the filter excludes every selected well —
 *   the result stays empty.
 * - `null` mapEntities (not loaded) or group mode: use the full expanded selection.
 * - Single mode: union in XDA-line wells that pass the filter gate.
 * - Group mode with an otherwise-empty result: fall back to line-intersecting UWIs.
 */
export function buildWellsForScene({
  selectedWells,
  mapEntities,
  isMapGroupMode,
  xdaLineWells,
  groupModeUwiColors
}: BuildWellsForSceneInput): Record<string, ISelectedWell> {
  const wellsForScene: Record<string, ISelectedWell> = {};

  if (mapEntities !== null && !isMapGroupMode) {
    if (mapEntities.length > 0) {
      // Intersect mapEntities with the current selection to guard against
      // a stale set after a well is deselected.
      const expanded = expandWellsForScene(selectedWells);
      const entityUwis = new Set(mapEntities.map((e) => e.uwi));
      for (const entity of mapEntities) {
        if (expanded[entity.uwi]) {
          wellsForScene[entity.uwi] = {
            Uwi: entity.uwi,
            color: entity.color,
            // Preserve group from selectedWells — MapEntity.group is a group-by
            // label, not the "leg"/"inj" subordinate indicator. Without this a
            // leg is no longer recognised as one downstream.
            group: expanded[entity.uwi].group ?? entity.group ?? ""
          };
        }
      }
      // Subordinate wells (leg/inj) may not appear in the mapEntities response
      // since the endpoint only returns directly-selected wells. Carry them
      // through so they render in the 3D scene — but a leg follows its parent:
      // if the parent PROD well is excluded by the active filter (absent from
      // mapEntities), drop the orphaned leg rather than render it stale-coloured.
      // Inj wells are auto-added and intentionally render regardless of filter.
      for (const [uwi, well] of Object.entries(expanded)) {
        if (!isSubordinateWell(well.group) || entityUwis.has(uwi)) continue;
        if (well.group === "leg" && well.parentUwi && !entityUwis.has(well.parentUwi)) {
          continue;
        }
        wellsForScene[uwi] = well;
      }
      // Fallback: mapEntities hasn't resolved for the current selection yet.
      if (Object.keys(wellsForScene).length === 0) {
        Object.assign(wellsForScene, expanded);
      }
    }
    // mapEntities is [] — filter excludes all selected wells; result stays empty
  } else {
    // mapEntities is null (not yet loaded) or group mode — use all selected wells
    Object.assign(wellsForScene, expandWellsForScene(selectedWells));
  }

  // Mirror XdaViewer's getXdaPayloadWells union: include wells that intersect
  // the XDA line but aren't already present. groupModeUwiColors (derived from
  // entitiesOnMap, keyed on txnId) acts as the filter gate — only wells present
  // there pass the active filter and get added.
  if (!isMapGroupMode) {
    for (const uwi of xdaLineWells ?? []) {
      if (!(uwi in wellsForScene) && uwi in groupModeUwiColors) {
        wellsForScene[uwi] = {
          Uwi: uwi,
          color: groupModeUwiColors[uwi],
          group: ""
        };
      }
    }
  }

  // In global group mode the map renders synthetic group entities and
  // selectedWells is empty. Fall back to the UWIs that intersect the drawn XDA
  // line — the same fallback XdaViewer uses via getXdaPayloadWells.
  if (Object.keys(wellsForScene).length === 0 && isMapGroupMode) {
    for (const uwi of xdaLineWells ?? []) {
      wellsForScene[uwi] = {
        color: groupModeUwiColors[uwi] ?? FALLBACK_GROUP_COLOR,
        Uwi: uwi,
        group: ""
      };
    }
  }

  return wellsForScene;
}
